// frontend/src/components/ads/InFeedAd.js
// In-feed native ad component - COMPACT LAYOUT with CTA on right

import React, { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
const BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BASE_URL}${url}`;
};

const InFeedAd = ({ ad, organizationId = null, playerId = null }) => {
  const hasTrackedImpression = useRef(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!ad?.id || hasTrackedImpression.current) return;
    const trackImpression = async () => {
      try {
        await fetch(`${API_URL}/ads/impression`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ adId: ad.id, organizationId, playerId, placement: 'in_feed' })
        });
        hasTrackedImpression.current = true;
      } catch (err) {
        console.error('Failed to track ad impression:', err);
      }
    };
    trackImpression();
  }, [ad?.id, organizationId, playerId]);

  if (!ad) return null;

  const bgColorFrom = ad.bgColorFrom || ad.bg_color_from || '#2d6a4f';
  const bgColorTo = ad.bgColorTo || ad.bg_color_to || '#1b4d3e';
  const ctaText = ad.ctaText || ad.cta_text || 'Learn More';
  const imageUrl = resolveImageUrl(ad.imageUrl || ad.image_url);
  const geoScope = ad.geoScope || ad.geo_scope || 'national';
  const badgeText = ad.badge || ad.badge_text;

  const handleClick = () => {
    const clickUrl = `${API_URL}/ads/click/${ad.id}?organizationId=${organizationId || ''}&playerId=${playerId || ''}`;
    window.open(clickUrl, '_blank', 'noopener,noreferrer');
  };

  // Line clamp style - allows wrap but limits lines
  const lineClamp = (lines) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
  });

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: `linear-gradient(135deg, ${bgColorFrom} 0%, ${bgColorTo} 100%)`,
        borderRadius: 8,
        padding: '8px 10px',
        marginBottom: 12,
        boxShadow: isHovered ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 6px rgba(0,0,0,0.1)',
        border: '1px solid rgba(255,255,255,0.1)',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Single row layout: Image | Text | CTA */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Image */}
        {imageUrl ? (
          <img src={imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.1)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 14 }}>🏌️</span>
          </div>
        )}

        {/* Text container - grows to fill space */}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          {/* Sponsor + Sponsored label row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 1 }}>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', ...lineClamp(1) }}>
              {ad.sponsor}{badgeText && geoScope === 'local' && ' 📍'}
            </span>
            <span style={{ fontSize: 8, fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              • Sponsored
            </span>
          </div>

          {/* Headline */}
          <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.2, ...lineClamp(1) }}>
            {ad.headline}
          </div>

          {/* Description - optional, smaller */}
          {ad.description && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', lineHeight: 1.2, marginTop: 1, ...lineClamp(1) }}>
              {ad.description}
            </div>
          )}
        </div>

        {/* CTA Button - right side */}
        <span style={{ 
          display: 'inline-block', 
          background: '#fff', 
          color: bgColorFrom, 
          padding: '5px 10px', 
          borderRadius: 4, 
          fontSize: 10, 
          fontWeight: 700,
          flexShrink: 0,
          whiteSpace: 'nowrap'
        }}>
          {ctaText} →
        </span>
      </div>
    </div>
  );
};

export default InFeedAd;
